import { useState, type ReactNode } from 'react';
import { formatMoney } from '../lib/format';

/**
 * Gráficos dos dashboards em SVG próprio (decisão Q3): barras agrupadas por
 * dia/mês, barras horizontais de ranking e funil. Sem biblioteca — o tooltip
 * é um rótulo simples que acompanha o item sob o mouse.
 */

export interface Series {
  key: string;
  label: string;
  color: string;
}

export function KpiCard({
  label,
  count,
  value,
  tone = 'slate',
  hint,
}: {
  label: string;
  count: number | string;
  value?: number;
  tone?: 'slate' | 'emerald' | 'red' | 'sky' | 'amber';
  hint?: string;
}) {
  const tones: Record<string, string> = {
    slate: 'text-slate-900',
    emerald: 'text-emerald-600',
    red: 'text-red-600',
    sky: 'text-sky-600',
    amber: 'text-amber-600',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${tones[tone]}`}>{count}</p>
      {value !== undefined && <p className="text-xs text-slate-500">{formatMoney(value)}</p>}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

export function ChartCard({
  title,
  children,
  action,
  className = '',
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`}>
      <header className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
        {action}
      </header>
      {children}
    </section>
  );
}

export function NoData({ text = 'Sem dados no período.' }: { text?: string }) {
  return <p className="py-8 text-center text-xs text-slate-400">{text}</p>;
}

/** Barras agrupadas: uma posição por rótulo, uma barra por série. */
export function GroupedBars<T extends Record<string, number | string>>({
  data,
  labelKey,
  series,
  formatLabel = (value) => value,
  height = 180,
}: {
  data: T[];
  labelKey: keyof T;
  series: Series[];
  formatLabel?: (value: string) => string;
  height?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(
    1,
    ...data.flatMap((item) => series.map((serie) => Number(item[serie.key]) || 0)),
  );
  const slot = 100 / Math.max(1, data.length);
  const barWidth = (slot * 0.8) / series.length;
  const step = Math.ceil(data.length / 10);
  const total = (key: string) => data.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);

  if (data.length === 0 || series.every((serie) => total(serie.key) === 0)) return <NoData />;

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-3 text-xs text-slate-500">
        {series.map((serie) => (
          <span key={serie.key} className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: serie.color }} />
            {serie.label} ({total(serie.key)})
          </span>
        ))}
      </div>
      <div className="relative">
        <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" className="h-44 w-full">
          {[0.25, 0.5, 0.75].map((line) => (
            <line
              key={line}
              x1="0"
              x2="100"
              y1={height * line}
              y2={height * line}
              stroke="#f1f5f9"
              strokeWidth="0.5"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {data.map((item, index) => (
            <g
              key={index}
              onMouseEnter={() => setHover(index)}
              onMouseLeave={() => setHover(null)}
            >
              <rect x={index * slot} y="0" width={slot} height={height} fill="transparent" />
              {series.map((serie, position) => {
                const value = Number(item[serie.key]) || 0;
                const barHeight = (value / max) * (height - 4);
                return (
                  <rect
                    key={serie.key}
                    x={index * slot + slot * 0.1 + position * barWidth}
                    y={height - barHeight}
                    width={Math.max(barWidth - 0.2, 0.3)}
                    height={barHeight}
                    fill={serie.color}
                    opacity={hover === null || hover === index ? 1 : 0.45}
                  />
                );
              })}
            </g>
          ))}
        </svg>
        {hover !== null && data[hover] && (
          <div
            className="pointer-events-none absolute top-0 z-10 rounded-lg bg-slate-900 px-2.5 py-1.5 text-[11px] text-white shadow"
            style={{ left: `${Math.min(80, hover * slot)}%` }}
          >
            <p className="font-medium">{formatLabel(String(data[hover][labelKey]))}</p>
            {series.map((serie) => (
              <p key={serie.key}>
                {serie.label}: {data[hover][serie.key]}
              </p>
            ))}
          </div>
        )}
      </div>
      {/* Rótulos soltos, centrados no item: um a cada `step`, sem cortar o texto. */}
      <div className="relative mt-1 h-4 text-[10px] text-slate-400">
        {data.map((item, index) =>
          index % step === 0 ? (
            <span
              key={index}
              className="absolute -translate-x-1/2 whitespace-nowrap"
              style={{ left: `${(index + 0.5) * slot}%` }}
            >
              {formatLabel(String(item[labelKey]))}
            </span>
          ) : null,
        )}
      </div>
    </div>
  );
}

/** Ranking em barras horizontais. */
export function HorizontalBars({
  items,
  color = 'var(--crm-primary)',
  empty,
}: {
  items: Array<{ key: string; label: string; value: number; display: string; detail?: string }>;
  color?: string;
  empty?: string;
}) {
  if (items.length === 0) return <NoData text={empty} />;
  const max = Math.max(1, ...items.map((item) => item.value));
  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item.key} className="text-sm">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-slate-700" title={item.label}>
              {item.label}
            </span>
            <span className="shrink-0 text-xs font-medium text-slate-600">{item.display}</span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-slate-100">
            <div
              className="h-2 rounded-full"
              style={{ width: `${(item.value / max) * 100}%`, backgroundColor: color }}
            />
          </div>
          {item.detail && <p className="mt-0.5 text-[11px] text-slate-400">{item.detail}</p>}
        </li>
      ))}
    </ul>
  );
}

/**
 * Funil: cada etapa é uma faixa centralizada, de largura proporcional a quem
 * passou por ela; à direita, valor, conversão e tempo médio.
 */
export function Funnel({
  stages,
}: {
  stages: Array<{
    key: string;
    name: string;
    color: string | null;
    entered: number;
    count: number;
    total: number;
    conversion: string;
    avgTime: string;
  }>;
}) {
  if (stages.length === 0) return <NoData text="A pipeline não tem etapas." />;
  const max = Math.max(1, ...stages.map((stage) => stage.entered));
  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-[1fr_auto] gap-3 text-[11px] uppercase tracking-wide text-slate-400">
        <span>Etapa · passaram</span>
        <span className="w-64 text-right">Agora · valor · conversão · tempo médio</span>
      </div>
      {stages.map((stage) => {
        const width = Math.max(8, (stage.entered / max) * 100);
        return (
          <div key={stage.key} className="grid grid-cols-[1fr_auto] items-center gap-3">
            <div className="flex justify-center">
              <div
                className="flex h-9 items-center justify-between rounded-md px-3 text-xs font-medium text-white"
                style={{
                  width: `${width}%`,
                  backgroundColor: stage.color ?? '#64748b',
                  minWidth: '7rem',
                }}
                title={`${stage.entered} negócio(s) passaram por ${stage.name}`}
              >
                <span className="truncate">{stage.name}</span>
                <span>{stage.entered}</span>
              </div>
            </div>
            <div className="grid w-64 grid-cols-4 gap-1 text-right text-xs text-slate-600">
              <span>{stage.count}</span>
              <span className="truncate">{formatMoney(stage.total)}</span>
              <span className="font-medium text-slate-800">{stage.conversion}</span>
              <span>{stage.avgTime}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
